import React from 'react';
import { motion } from 'framer-motion';
import { Send, PhoneCall, Star, Receipt } from 'lucide-react';
import NeuralNet from '../effects/NeuralNet';

const OFFERINGS = [
  {
    icon: Send,
    title: 'Lead Generation & Outreach',
    setup: '$750 setup',
    monthly: '$199/mo',
    desc: 'Find businesses in your target market and reach out with personalized email — automatically. Includes the sending infrastructure (alt-domain mailboxes, warmup, rotation) so your messages actually land in inboxes, not spam.',
    bullets: ['Targeted prospect discovery', 'Multi-mailbox rotation for deliverability', 'Personalized sequences with reply detection'],
  },
  {
    icon: PhoneCall,
    title: 'AI Receptionist & Missed-Call Recovery',
    setup: '$500 setup',
    monthly: '$79/mo',
    desc: 'Stop losing jobs to voicemail. Missed calls trigger an instant text back, and a chatbot on your site answers hours, services, and quote questions 24/7.',
    bullets: ['Instant SMS reply on missed calls', 'On-site chatbot trained on your business', 'Routes urgent leads straight to your phone'],
  },
  {
    icon: Star,
    title: 'Review Monitoring & Response',
    setup: '$300 setup',
    monthly: '$49/mo',
    desc: 'Get alerted when a new Google or Yelp review lands, with an on-brand reply already drafted. You approve in 30 seconds — your reputation stays sharp.',
    bullets: ['Watches Google, Yelp & Facebook', 'Drafts on-brand replies for approval', 'Flags negative reviews immediately'],
  },
  {
    icon: Receipt,
    title: 'Invoice & Payment Follow-up',
    setup: '$300 setup',
    monthly: '$49/mo',
    desc: 'Invoices sent the moment a job wraps, polite reminders for unpaid ones, and a clean dashboard so you always know who owes what. Get paid faster, stop chasing.',
    bullets: ['Auto-send invoices via Stripe', 'Scheduled, polite payment reminders', 'Clear AR dashboard at a glance'],
  },
];

function GrowthAutomation() {
  return (
    <section id="automation" className="relative py-24 border-t border-border/40 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none hidden md:block"
        style={{
          maskImage: 'linear-gradient(to bottom, transparent 0, black 96px, black calc(100% - 96px), transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, black 96px, black calc(100% - 96px), transparent 100%)',
        }}
      >
        <NeuralNet />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-14">
          <p className="text-sm text-accent-2 font-medium mb-3 uppercase tracking-wider">AI agents on the job</p>
          <h2 className="font-display font-bold text-4xl md:text-5xl text-text mb-4">
            Agentic AI that works like an extra employee.
          </h2>
          <p className="text-text-muted text-lg">
            Beyond the website — AI agents and done-for-you automation that bring in leads, answer customers, respond to reviews, and chase invoices while you focus on the actual work. One-time setup, small monthly fee.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {OFFERINGS.map((o, i) => (
            <motion.div
              key={o.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '0px 0px 300px 0px' }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="group relative glass rounded-2xl p-7 hover:border-accent/40 transition-colors"
            >
              <div className="absolute -inset-px rounded-2xl bg-gradient-brand opacity-0 group-hover:opacity-10 transition-opacity pointer-events-none" />
              <div className="relative">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-gradient-brand-soft flex items-center justify-center border border-border flex-shrink-0">
                    <o.icon size={20} className="text-accent-2" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-semibold text-xl text-text mb-1">{o.title}</h3>
                    <p className="text-xs text-accent-2 font-medium">
                      {o.setup} <span className="text-text-muted">·</span> {o.monthly}
                    </p>
                  </div>
                </div>
                <p className="text-text-muted text-sm mb-5 leading-relaxed">{o.desc}</p>
                <ul className="space-y-2">
                  {o.bullets.map((b) => (
                    <li key={b} className="text-sm text-text flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-accent-2 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-text-muted">
            Combining services? <a href="#contact" className="text-accent-2 hover:underline">Ask about a bundle</a> — most businesses save when they stack two or more.
          </p>
        </div>
      </div>
    </section>
  );
}

export default GrowthAutomation;
